import { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Card, CardContent } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Calendar as CalendarIcon,
  FileDown,
  Search,
  ShoppingCart,
  DollarSign,
  CreditCard,
  AlertCircle,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  get_sales_reports_res,
  server_post_data,
} from "../ServiceConnection/serviceconnection";
import XLSX from "sheetjs-style";
import {
  handleError,
  inputdateformateChange,
} from "../CommonJquery/CommonJquery";

export default function SalesReport() {
  const { toast } = useToast();

  // Date states
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  sevenDaysAgo.setHours(13, 0, 0, 0); // default 1:00 PM

  const defaultEnd = new Date();
  defaultEnd.setHours(3, 0, 0, 0); // default 3:00 AM

  const [filterOption, setFilterOption] = useState("");
  const [startCal, setStartCal] = useState(new Date());
  const [endCal, setEndCal] = useState(new Date());

  // Data states
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [salesData, setSalesData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;

  // Stats
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    totalTax: 0,
    totalDiscount: 0,
  });

  // Format number
  const formatNumber = (num) => {
    if (num === "-") return "-";
    if (num === undefined || num === null) return 0;
    const parsed = parseFloat(num);
    if (isNaN(parsed)) return 0;
    // If decimal part is zero, return integer; otherwise show 2 decimal places
    return parsed % 1 === 0 ? parsed : parseFloat(parsed.toFixed(2));
  };

  // Fetch data
  const fetchSalesData = useCallback(
    async (start_date, end_date, flag = "1", call_id = "0") => {
      // Handle date filtering
      if (flag === "2") {
        if (filterOption === "today") {
          const today = new Date();
          start_date = new Date(today.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_week") {
          const today = new Date();
          const firstDayOfWeek = new Date(
            today.setDate(today.getDate() - today.getDay()),
          );
          start_date = new Date(firstDayOfWeek.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_month") {
          const today = new Date();
          start_date = new Date(
            today.getFullYear(),
            today.getMonth(),
            1,
            0,
            0,
            0,
          );
          end_date = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0,
            23,
            59,
            59,
          );
        } else if (filterOption === "this_year") {
          const today = new Date();
          start_date = new Date(today.getFullYear(), 0, 1, 0, 0, 0);
          end_date = new Date(today.getFullYear(), 11, 31, 23, 59, 59);
        } else if (filterOption === "custom") {
          if (!startCal || !endCal) {
            toast({
              title: "Validation Error",
              description: "Please select both start and end dates.",
              variant: "destructive",
            });
            return;
          }
          // Merge selected date + time into the variables used for the API call
          start_date = startCal;
          end_date = endCal;
        }

        if (start_date > end_date) {
          toast({
            title: "Validation Error",
            description: "Start date cannot be greater than end date.",
            variant: "destructive",
          });
          return;
        }
      }

      setShowLoaderAdmin(true);
      const fd = new FormData();
      const formatDateLocal = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      fd.append("start_date", formatDateLocal(start_date));
      fd.append("end_date", formatDateLocal(end_date));
      fd.append("flag", flag);
      fd.append("call_id", call_id);

      try {
        const response = await server_post_data(get_sales_reports_res, fd);

        if (response.data.error) {
          toast({
            title: "Error",
            description: response.data.message,
            variant: "destructive",
          });
        } else {
          const data = response.data.message.sales_data || [];
          setSalesData(data);

          // Calculate stats
          const totalRevenue = data.reduce(
            (sum, item) => sum + parseFloat(item.final_amount || 0),
            0,
          );
          const totalTax = data.reduce(
            (sum, item) => sum + parseFloat(item.tax_amount || 0),
            0,
          );
          const totalDiscount = data.reduce(
            (sum, item) => sum + parseFloat(item.discount_amount || 0),
            0,
          );

          setStats({
            totalOrders: data.length,
            totalRevenue: totalRevenue,
            totalTax: totalTax,
            totalDiscount: totalDiscount,
          });
        }
      } catch (error) {
        console.error("Error fetching sales data:", error);
        toast({
          title: "Error",
          description: "Failed to load sales data",
          variant: "destructive",
        });
      } finally {
        setShowLoaderAdmin(false);
      }
    },
    [filterOption, startCal, endCal, toast],
  );

  // Handle search
  const handleSearch = useCallback(() => {
    setCurrentPage(1);
    fetchSalesData(startCal, endCal, "2", "0");
  }, [startCal, endCal, fetchSalesData]);

  // Export to Excel

  const getOrderTypeLabel = (orderType) => {
    const type = parseInt(orderType);
    switch (type) {
      case 0:
        return "QR Order";
      case 1:
        return "Takeaway";
      case 2:
        return "Delivery";
      case 3:
        return "Dine In";
      default:
        return "Other";
    }
  };


  const getPaymentModeLabel = (paymentMode) => {
    if (!paymentMode) return "N/A";
    if (typeof paymentMode === "string" && paymentMode.startsWith("[")) {
      try {
        const parsed = JSON.parse(paymentMode);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.map((p) => p.payment_mode || p).join(", ");
        }
      } catch (e) {
        console.error("Error parsing payment mode:", e);
      }
    }
    return paymentMode;
  };
  function downloadExcel(orderData, filename = "Sales_Report.xlsx") {
    if (!orderData.length) {
      handleError("No data available for export!");
      return;
    }

    const formattedOrderData = orderData.map((item, index) => {
      let cgst = 0,
        sgst = 0,
        vat = 0;
      try {
        const taxes = JSON.parse(item.tax_breakup_json);
        Object.values(taxes).forEach((tax) => {
          const taxName = (tax.name || "").toLowerCase();
          if (taxName.includes("vat")) {
            vat += Number(tax.cgst || 0) + Number(tax.sgst || 0);
          } else if (taxName.includes("default") || taxName.includes("gst")) {
            cgst += Number(tax.cgst || 0);
            sgst += Number(tax.sgst || 0);
          }
        });
      } catch (e) {}

      return {
        "Sr. No.": index + 1,
        "Order No.": item.primary_id || "-",
        "Table No.":
          parseInt(item.order_type) === 3 || parseInt(item.order_type) === 0
            ? item.table_code_names
            : getOrderTypeLabel(item.order_type),
        Date: inputdateformateChange(item.entry_date),
        "Payment Type": getPaymentModeLabel(item.payment_mode_new),
        "Payment Collect": getPaymentModeLabel(item.payment_modes),
        "Order Type": getOrderTypeLabel(item.order_type),
        "Product Amount (₹)": formatNumber(item.product_amount),
        "Food Amount (₹)": formatNumber(item.product_gst_amount),
        "Container Charge (₹)": formatNumber(item.packing_amount),
        "Delivery Charge (₹)": formatNumber(item.delivery_amount),
        "CGST (₹)": formatNumber(cgst),
        "SGST (₹)": formatNumber(sgst),
        "Liquor Amount (₹)": formatNumber(item.product_vat_amount),
        "VAT (₹)": formatNumber(vat),
        "Service Charge (₹)": formatNumber(item.service_amount),
        "Discount Amount (₹)": formatNumber(
          parseFloat(item.discount_amount || 0) +
            parseFloat(item.discount_amount_extrnal || 0),
        ),
        "Round Off (₹)": formatNumber(item.roundoff_amount),
        "Waived Off (₹)": formatNumber(item.waived_off),
        "Total Amount (₹)": formatNumber(item.final_amount),
      };
    });

    // Totals row
    const totals = calculateTotals();
    let totalCgst = 0,
      totalSgst = 0,
      totalVat = 0;
    orderData.forEach((item) => {
      try {
        const taxes = JSON.parse(item.tax_breakup_json);
        Object.values(taxes).forEach((tax) => {
          const taxName = (tax.name || "").toLowerCase();
          if (taxName.includes("vat")) {
            totalVat += Number(tax.cgst || 0) + Number(tax.sgst || 0);
          } else if (taxName.includes("default") || taxName.includes("gst")) {
            totalCgst += Number(tax.cgst || 0);
            totalSgst += Number(tax.sgst || 0);
          }
        });
      } catch (e) {}
    });

    formattedOrderData.push({
      "Sr. No.": "",
      "Order No.": "",
      "Table No.": "",
      Date: "",
      "Payment Type": "",
      "Order Type": "TOTAL",
      "Product Amount (₹)": formatNumber(totals.product_amount),
      "Food Amount (₹)": formatNumber(totals.product_gst_amount), // was blank
      "Container Charge (₹)": formatNumber(totals.packing_amount), // was blank
      "Delivery Charge (₹)": formatNumber(totals.delivery_amount),
      "CGST (₹)": formatNumber(totalCgst),
      "SGST (₹)": formatNumber(totalSgst),
      "Liquor Amount (₹)": formatNumber(totals.product_vat_amount), // was blank
      "VAT (₹)": formatNumber(totalVat),
      "Service Charge (₹)": formatNumber(totals.service_amount),
      "Discount Amount (₹)": formatNumber(totals.discount_amount),
      "Round Off (₹)": formatNumber(totals.roundoff_amount), // was blank
      "Waived Off (₹)": formatNumber(totals.waived_off), // was blank
      "Total Amount (₹)": formatNumber(totals.final_amount),
    });

    const orderSheet = XLSX.utils.json_to_sheet(formattedOrderData);
    orderSheet["!cols"] = [
      { wch: 8 },
      { wch: 8 },
      { wch: 12 },
      { wch: 15 },
      { wch: 15 },
      { wch: 12 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
      { wch: 15 },
      { wch: 12 },
      { wch: 12 },
      { wch: 15 },
      { wch: 16 },
      { wch: 18 },
      { wch: 13 },
      { wch: 13 },
      { wch: 18 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, orderSheet, "Sales Report");
    XLSX.writeFile(workbook, filename);
  }

  // Pagination
  const totalPages = Math.ceil(salesData.length / entriesPerPage);
  const currentData = salesData.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage,
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const calculateTotals = () => {
    return salesData.reduce(
      (acc, row) => ({
        product_amount:
          acc.product_amount + (parseFloat(row.product_amount) || 0),
        packing_amount:
          acc.packing_amount + (parseFloat(row.packing_amount) || 0), // ADD
        discount_amount:
          acc.discount_amount +
          ((parseFloat(row.discount_amount) || 0) +
            (parseFloat(row.discount_amount_extrnal) || 0)),
        delivery_amount:
          acc.delivery_amount + (parseFloat(row.delivery_amount) || 0),
        tax_amount: acc.tax_amount + (parseFloat(row.tax_amount) || 0),
        service_amount:
          acc.service_amount + (parseFloat(row.service_amount) || 0),
        tip_amount: acc.tip_amount + (parseFloat(row.tip_amount) || 0),
        roundoff_amount:
          acc.roundoff_amount + (parseFloat(row.roundoff_amount) || 0), // ADD
        waived_off: acc.waived_off + (parseFloat(row.waived_off) || 0), // ADD
        product_gst_amount:
          acc.product_gst_amount + (parseFloat(row.product_gst_amount) || 0), // ADD
        product_vat_amount:
          acc.product_vat_amount + (parseFloat(row.product_vat_amount) || 0), // ADD
        final_amount: acc.final_amount + (parseFloat(row.final_amount) || 0),
      }),
      {
        product_amount: 0,
        packing_amount: 0,
        discount_amount: 0,
        delivery_amount: 0,
        tax_amount: 0,
        service_amount: 0,
        tip_amount: 0,
        roundoff_amount: 0,
        waived_off: 0,
        product_gst_amount: 0,
        product_vat_amount: 0,
        final_amount: 0,
      },
    );
  };

  // Initial load
  useEffect(() => {
    fetchSalesData(startCal, endCal, "1", "0");
  }, []);

  return (
    <MainLayout>
      <Header
        title="Sales Report"
        subtitle="View and analyze sales transactions and revenue"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.totalOrders}
                </p>
                <p className="text-sm text-muted-foreground">Total Orders</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                ₹{stats.totalRevenue % 1 === 0 ? stats.totalRevenue : stats.totalRevenue.toFixed(2)}
                </p>
                <p className="text-sm text-muted-foreground">Total Revenue</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                ₹{stats.totalTax % 1 === 0 ? stats.totalTax : stats.totalTax.toFixed(2)}

                </p>
                <p className="text-sm text-muted-foreground">Total Tax</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                ₹{stats.totalDiscount % 1 === 0 ? stats.totalDiscount : stats.totalDiscount.toFixed(2)}
                </p>
                <p className="text-sm text-muted-foreground">Total Discount</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Filter Dropdown */}
              <div className="space-y-2">
                <Label>Filter</Label>
                <Select value={filterOption} onValueChange={setFilterOption}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="this_week">This Week</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                    <SelectItem value="this_year">This Year</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !startCal && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startCal ? (
                          format(startCal, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startCal}
                        onSelect={setStartCal}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !endCal && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endCal ? (
                          format(endCal, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endCal}
                        onSelect={setEndCal}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {/* Action Buttons */}
              <div
                className={cn(
                  "flex items-end gap-2",
                  filterOption !== "custom" && "md:col-span-3",
                )}
              >
                <Button
                  onClick={handleSearch}
                  className="gap-2"
                  disabled={showLoaderAdmin}
                >
                  <Search className="w-4 h-4" />
                  Search
                </Button>
                <Button
                  variant="outline"
                  onClick={() => downloadExcel(salesData)}
                  className="gap-2"
                  disabled={salesData.length === 0}
                >
                  <FileDown className="w-4 h-4" />
                  Export Excel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table Card */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground">Sr.No</TableHead>
                  <TableHead className="text-muted-foreground">
                    Order No.
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Table No.
                  </TableHead>
                  <TableHead className="text-muted-foreground">Date</TableHead>
                  <TableHead className="text-muted-foreground">
                    Payment Type
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Payment Collect
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Order Type
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Product Amount (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Food Amount (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Container Charge (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Delivery (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    CGST (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    SGST (₹)
                  </TableHead>

                  <TableHead className="text-muted-foreground text-right">
                    Liquor Amount (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    {" "}
                    VAT (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Service Charge (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Discount (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Round Off (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Waived Off (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Total (₹)
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {showLoaderAdmin ? (
                  <TableRow>
                    <TableCell
                      colSpan={18}
                      className="text-center text-muted-foreground"
                    >
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : currentData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={18}
                      className="text-center text-muted-foreground"
                    >
                      No data found
                    </TableCell>
                  </TableRow>
                ) : (
                  currentData.map((row, index) => {
                    // Tax breakup parse
                    let cgst = 0,
                      sgst = 0,
                      vat = 0;
                    try {
                      const taxes = JSON.parse(row.tax_breakup_json);
                      Object.values(taxes).forEach((tax) => {
                        const taxName = (tax.name || "").toLowerCase();
                        if (taxName.includes("vat")) {
                          vat += Number(tax.cgst || 0) + Number(tax.sgst || 0);
                        } else if (
                          taxName.includes("default") ||
                          taxName.includes("gst")
                        ) {
                          cgst += Number(tax.cgst || 0);
                          sgst += Number(tax.sgst || 0);
                        }
                      });
                    } catch (e) {}

                    return (
                      <TableRow key={index} className="border-border">
                        <TableCell>
                          {(currentPage - 1) * entriesPerPage + index + 1}
                        </TableCell>
                        <TableCell className="font-medium">
                          OR-{row.primary_id}
                        </TableCell>
                        <TableCell>
                          {parseInt(row.order_type) === 3 || parseInt(row.order_type) === 0
                            ? row.table_code_names
                            : getOrderTypeLabel(row.order_type)}
                        </TableCell>
                        <TableCell>{formatDate(row.entry_date)}</TableCell>
                        <TableCell>
              <span className="inline-flex items-center px-2 py-1 rounded-md bg-primary/10 text-primary text-xs font-medium">
                            {getPaymentModeLabel(row.payment_mode_new)}
                          </span>
                        </TableCell>
                        <TableCell>
              <span className="inline-flex items-center px-2 py-1 rounded-md bg-primary/10 text-primary text-xs font-medium">
                            {getPaymentModeLabel(row.payment_modes)}
                          </span>
                        </TableCell>
                        <TableCell>
                <span className="inline-flex items-center px-2 py-1 rounded-md bg-success/10 text-success text-xs font-medium">
                            {getOrderTypeLabel(row.order_type)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(row.product_amount)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(row.product_gst_amount)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(row.packing_amount)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(row.delivery_amount)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(cgst)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(sgst)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(row.product_vat_amount)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(vat)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(row.service_amount)}
                        </TableCell>
                        <TableCell className="text-right text-destructive">
                          {formatNumber(
                            parseFloat(row.discount_amount || 0) +
                              parseFloat(row.discount_amount_extrnal || 0),
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(row.roundoff_amount)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(row.waived_off)}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatNumber(row.final_amount)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * entriesPerPage + 1} to{" "}
                {Math.min(currentPage * entriesPerPage, salesData.length)} of{" "}
                {salesData.length} entries
              </div>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 &&
                      pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <Button
                        key={pageNumber}
                        variant={
                          pageNumber === currentPage ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </Button>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return <span key={pageNumber}>...</span>;
                  }
                  return null;
                })}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Notes Section */}
        <Card className="border-destructive/20 bg-destructive/5">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm font-semibold text-destructive flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Important Notes:
              </p>
              <ul className="text-xs text-destructive space-y-1 list-disc list-inside ml-4">
                <li>
                  If bill is settled then "Settlement Amount" is used as "Total"
                  amount and it will not impact My Amount calculation.
                </li>
                <li>Current status of Tax: A ⇒ Active, I ⇒ Inactive</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
